import { useRef } from 'preact/hooks';
import { BASE_PATH } from '../../shared/types';
import { useMe } from '../lib/auth';
import { AccountMenu } from '../components/AccountMenu';
import { HelpLink } from '../components/HelpLink';
import { NativeLink } from '../components/NativeLink';

const STEPS = [
  { title: 'Create your event.', body: 'Add dates and organizer details, choose stake or ward level, and include questions for T-shirt sizes, carpools, and other event details.' },
  { title: 'Connect Google.', body: 'Create a spreadsheet and folder in your Drive to receive registrations and signed forms.' },
  { title: 'Share the link.', body: 'Parents and participants complete the form, sign it, then review and submit. Responses arrive in your Sheet, with signed PDFs in your Drive.' },
];

const ORGANIZERS = [
  'Reuse details from past events to set up the next one faster.',
  'Add questions for T-shirt sizes, carpools, and other event details.',
  'Publish and close registration whenever you like.',
  'Record participants’ wards in your registration spreadsheet.',
];

const PARENTS = [
  'Works on your phone. No app or account required.',
  'Complete the familiar form one step at a time.',
  'Read the completed form, then draw or type your signature.',
  'Review everything and download a signed copy before you submit.',
];

type IconName = 'forms' | 'clipboard' | 'phone' | 'shield' | 'arrow' | 'chevron' | 'close';

function LandingIcon({ name }: { name: IconName }) {
  return (
    <svg class="landing-icon" width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      {name === 'forms' && <><path d="M14 2H8a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6M2 6v14a2 2 0 0 0 2 2h10" /></>}
      {name === 'clipboard' && <><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2M8 14l3 3 5-6" /></>}
      {name === 'phone' && <><rect x="6" y="2" width="12" height="20" rx="2" /><path d="M11 18h2" /></>}
      {name === 'shield' && <><path d="M12 3 3 7v5c0 5 9 9 9 9s9-4 9-9V7Z" /><path d="m8 12 3 3 5-6" /></>}
      {name === 'arrow' && <path d="M4 12h16m-6-6 6 6-6 6" />}
      {name === 'chevron' && <path d="m9 6 6 6-6 6" />}
      {name === 'close' && <path d="m6 6 12 12M6 18 18 6" />}
    </svg>
  );
}

export function Home() {
  const { me } = useMe();
  const sampleDialog = useRef<HTMLDialogElement>(null);
  const deleted = new URLSearchParams(location.search).get('deleted');
  const eventUrl = `${BASE_PATH}/${me ? 'dashboard' : 'login'}`;
  const eventLabel = me ? 'Go to your events' : 'Create an event';
  const openSample = () => sampleDialog.current?.showModal();

  return (
    <main class="landing">
      <header class="landing-header">
        <a class="landing-brand" href={`${BASE_PATH}/`}><LandingIcon name="forms" />Gathered In Forms</a>
        <nav class="brandbar-actions" aria-label="Main">
          <HelpLink />
          {me ? <AccountMenu email={me.email} isAdmin={me.isAdmin} compactLabel="Account" />
            : <a class="landing-signin" href={`${BASE_PATH}/login`}>Sign in</a>}
        </nav>
      </header>
      {deleted && <p class="notice landing-notice">Your account has been deleted.</p>}

      <section class="hero">
        <div class="hero-text">
          <p class="eyebrow">Registration, made simple.</p>
          <h1>Planning the activity<br />is enough work.</h1>
          <p class="subhead">For <strong>Youth Conferences</strong>, <strong>Aaronic Priesthood Camps</strong>, <strong>Young Women Camps</strong>, and other ward and stake activities.</p>
          <p class="subhead hero-outcome">Collect registrations and signatures online, with responses and signed permission forms delivered to your Google Sheet and Drive.</p>
          <div class="cta">
            <div class="landing-actions">
              <a class="button big" href={eventUrl}>{eventLabel} <LandingIcon name="arrow" /></a>
              <a class="landing-text-link" href="#how-title" {...({ native: '' } as Record<string, string>)}>See how it works</a>
            </div>
            <p class="cta-note"><strong>Free to use.</strong>{!me && <> Sign in by email, no password needed.<br />Connect a Google account to receive registrations.</>}</p>
          </div>
        </div>
        <div class="hero-benefits">
          <div><LandingIcon name="clipboard" /><h2>For organizers</h2><p>Create your event, share one link, and receive registrations and signed forms in your own Google Drive.</p></div>
          <div><LandingIcon name="phone" /><h2>For parents and participants</h2><p>Use the link your organizer sent you. Register, sign, and submit on your phone. No app or account required.</p></div>
        </div>
      </section>

      <p class="landing-trust"><LandingIcon name="shield" />Your registrations. Your Google Drive.</p>

      <section class="how" aria-labelledby="how-title">
        <p class="eyebrow">From planning to signed forms</p>
        <h2 id="how-title">How it works</h2>
        <ol class="steps">
          {STEPS.map((s, i) => (
            <li class="step" key={s.title}>
              <span class="step-num" aria-hidden="true">{i + 1}</span>
              <div><h3>{s.title}</h3><p>{s.body}</p></div>
            </li>
          ))}
        </ol>
      </section>

      <section class="form-showcase" aria-labelledby="form-title">
        <div>
          <p class="eyebrow">Familiar form. Simpler process.</p>
          <h2 id="form-title">The permission form you know,<br />without the paper chase.</h2>
          <p>The Permission and Medical Release Form is built in. Families complete it one step at a time, sign it, then review and submit, on any device.</p>
          <p>You receive each registration in your Google Sheet and its signed PDFs in your Drive.</p>
          <div class="family-steps" aria-label="Participant process"><span>Complete</span><LandingIcon name="chevron" /><span>Sign</span><LandingIcon name="chevron" /><span>Review and submit</span></div>
        </div>
        <figure>
          <button class="form-preview-trigger link" type="button" aria-label="View sample permission form" aria-haspopup="dialog" aria-controls="sample-form-dialog" onClick={openSample}>
            <img class="form-preview" src={`${BASE_PATH}/landing-form.webp`} width={560} height={783}
              loading="lazy" alt="Example of a completed and signed Permission and Medical Release Form" />
          </button>
          <figcaption><button class="landing-text-link link" type="button" aria-haspopup="dialog" aria-controls="sample-form-dialog" onClick={openSample}>View sample form <LandingIcon name="arrow" /></button></figcaption>
        </figure>
      </section>

      <section class="audiences">
        <div class="audience">
          <h2>Less admin for organizers</h2>
          <ul class="checks">{ORGANIZERS.map((t) => <li key={t}>{t}</li>)}</ul>
        </div>
        <div class="audience">
          <h2>Easy for families</h2>
          <ul class="checks">{PARENTS.map((t) => <li key={t}>{t}</li>)}</ul>
        </div>
      </section>

      <section class="data-callout" aria-labelledby="data-title">
        <h2 id="data-title"><LandingIcon name="shield" />Your data stays yours</h2>
        <p>Registrations go to your own Google Sheet and Drive. Participant information isn’t kept on our servers. You can disconnect your Google account at any time.</p>
        <NativeLink class="landing-text-link" href={`${BASE_PATH}/privacy`}>Read our privacy policy <LandingIcon name="arrow" /></NativeLink>
      </section>

      <section class="landing-closing" aria-labelledby="closing-title">
        <p class="eyebrow">More time for the activity</p>
        <h2 id="closing-title">Ready to simplify registration?</h2>
        <p>Create your event, share one link, and let the forms take care of themselves.</p>
        <a class="button big" href={eventUrl}>{eventLabel} <LandingIcon name="arrow" /></a>
        <p class="cta-note">Free to use.{!me && ' Email sign-in and a Google account for organizers.'}</p>
      </section>

      <dialog class="sample-form-dialog" id="sample-form-dialog" ref={sampleDialog} aria-labelledby="sample-form-title">
        <div class="sample-form-heading">
          <h2 id="sample-form-title">Sample permission form</h2>
          <button type="button" class="secondary sample-form-close" aria-label="Close sample form" onClick={() => sampleDialog.current?.close()}><LandingIcon name="close" /></button>
        </div>
        <img src={`${BASE_PATH}/landing-form.webp`} width={560} height={783} alt="Sample completed and signed Permission and Medical Release Form" />
      </dialog>
    </main>
  );
}
